// Gemini AI Configuration
export const GEMINI_CONFIG = {
  API_KEY: process.env.REACT_APP_GEMINI_API_KEY || '',
  API_URL: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent',
  MODEL: 'gemini-1.5-flash',
  FALLBACK_URL: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-pro:generateContent',
  FALLBACK_MODEL: 'gemini-1.5-pro',
  MAX_TOKENS: 1000,
  TEMPERATURE: 0.3, // Lower temperature for more focused analysis
};

// Trading analysis prompt template
export const ANALYSIS_PROMPT = `
You are an expert financial trader, technical analyst, and fundamental analyst with 20+ years of experience in forex, cryptocurrency, and commodities markets. 

Analyze the following trading instrument and provide a comprehensive technical AND fundamental analysis:

Instrument: {instrument}
Timeframe: {timeframe}
Current Market Context: {context}

IMPORTANT: Respond with ONLY valid JSON, no markdown formatting, no explanations, no code blocks. Return raw JSON only.

PRICE CONTEXT for current market levels (December 2024):
- XAUUSD (Gold): Currently trading around $2300-$2450 range
- XAGUSD (Silver): Currently trading around $28-$35 range  
- BTCUSD: Currently trading around $90,000-$105,000 range
- ETHUSD: Currently trading around $3,200-$4,200 range
- Major forex pairs: Use current realistic levels

Provide your analysis in this exact JSON format:

{
  "sentiment": "bullish" | "bearish" | "neutral",
  "confidence": number (0-100),
  "analysis": "Detailed technical analysis including key indicators, patterns, and market structure",
  "fundamentalAnalysis": {
    "economicFactors": "Current economic factors affecting this instrument (interest rates, inflation, economic data, etc.)",
    "marketDrivers": "Key fundamental drivers currently impacting price (central bank policies, geopolitical events, supply/demand, etc.)",
    "outlook": "Short to medium-term fundamental outlook based on upcoming events and economic conditions",
    "sentiment": "bullish" | "bearish" | "neutral",
    "strength": number (0-100)
  },
  "entryPoints": ["specific entry recommendations with realistic current prices"],
  "exitPoints": ["take profit and stop loss levels with realistic current prices"],
  "riskLevel": "low" | "medium" | "high",
  "recommendation": "Overall trading recommendation combining both technical and fundamental analysis with realistic price levels",
  "keyLevels": {
    "support": ["realistic support levels based on current market prices"],
    "resistance": ["realistic resistance levels based on current market prices"]
  },
  "indicators": {
    "rsi": "current RSI reading and interpretation",
    "macd": "MACD signal and momentum",
    "bollinger": "Bollinger Bands position and volatility"
  }
}

Analysis requirements:
TECHNICAL ANALYSIS:
- Use CURRENT market price levels in all recommendations
- Focus on technical indicators (RSI, MACD, Bollinger Bands, Moving Averages)
- Identify chart patterns and market structure
- Provide specific price levels for entries and exits that match current market reality
- Include risk-reward ratios
- Assess market sentiment and momentum

FUNDAMENTAL ANALYSIS:
- Analyze current economic conditions affecting the instrument
- Consider central bank policies and interest rate outlook
- Assess geopolitical factors and market sentiment
- Evaluate supply/demand fundamentals
- Consider upcoming economic events and data releases
- Provide macroeconomic context for price movements

COMBINED ASSESSMENT:
- Align technical and fundamental views
- Highlight any divergences between technical and fundamental outlook
- Provide comprehensive risk assessment
- Be specific and actionable for traders

CRITICAL: Return only the JSON object, no other text, no markdown, no explanations. Use realistic current market prices.
`;

export interface GeminiAnalysisRequest {
  instrument: string;
  timeframe: string;
  context?: string;
}

export interface GeminiAnalysisResponse {
  sentiment: 'bullish' | 'bearish' | 'neutral';
  confidence: number;
  analysis: string;
  fundamentalAnalysis: {
    economicFactors: string;
    marketDrivers: string;
    outlook: string;
    sentiment: 'bullish' | 'bearish' | 'neutral';
    strength: number;
  };
  entryPoints: string[];
  exitPoints: string[];
  riskLevel: 'low' | 'medium' | 'high';
  recommendation: string;
  keyLevels: {
    support: string[];
    resistance: string[];
  };
  indicators: {
    rsi: string;
    macd: string;
    bollinger: string;
  };
} 